/**
 * <legal-tab-group> — Shared FAQ / Terms tab navigation wrapper.
 *
 * Mobile: initializes Swiper for horizontal tab pills.
 * Desktop (lap_and_up): plain flex column — Swiper is never initialized.
 *
 * Loaded from `sections/section-terms.liquid` and `sections/section-faq.liquid`.
 */

import { responsive } from '@theme/responsive';

const STACKED_CLASS = 'legal-tabs--stacked';

class LegalTabGroup extends HTMLElement {
  #swiper = null;
  #track = null;
  #onBreakpoint = () => this.#syncLayout();

  connectedCallback() {
    this.#track = this.querySelector('[data-legal-tabs-track]');

    responsive.addBreakpointListener('lap_and_up');
    document.body.addEventListener('breakpoint:lap_and_up', this.#onBreakpoint);
    this.#syncLayout();
  }

  disconnectedCallback() {
    document.body.removeEventListener('breakpoint:lap_and_up', this.#onBreakpoint);
    this.#destroySwiper();
    this.#track = null;
  }

  #syncLayout() {
    const isDesktop = responsive.matchesBreakpoint('lap_and_up');

    if (isDesktop) {
      this.#destroySwiper();
      this.classList.add(STACKED_CLASS);
      return;
    }

    this.classList.remove(STACKED_CLASS);
    this.#initSwiper();
  }

  #initSwiper() {
    if (this.#swiper || !this.#track) return;

    let config;
    try {
      config = JSON.parse(this.#track.getAttribute('data-swiper-settings') || '{}');
    } catch (error) {
      console.error('<legal-tab-group>: invalid data-swiper-settings JSON', error);
      return;
    }

    import('@theme/swiper')
      .then(({ default: Swiper }) => {
        if (this.#swiper || responsive.matchesBreakpoint('lap_and_up') || !this.#track) return;

        this.#swiper = new Swiper(this.#track, config);
        this.#bindEdgeFade(this.#swiper);
      })
      .catch((error) => {
        console.error('<legal-tab-group>: failed to load Swiper', error);
      });
  }

  #destroySwiper() {
    if (!this.#swiper) {
      this.#clearInlineStyles();
      return;
    }

    this.#swiper.destroy(true, true);
    this.#swiper = null;
    this.#clearInlineStyles();
  }

  #clearInlineStyles() {
    const list = this.#track?.querySelector('.legal-tabs__list');

    if (list) {
      list.style.transform = '';
      list.style.transitionDuration = '';
    }

    this.#track?.querySelectorAll('.legal-tabs__item').forEach((item) => {
      item.style.width = '';
      item.style.marginRight = '';
    });
  }

  #bindEdgeFade(swiper) {
    if (!this.#track?.classList.contains('swiper--edge-fade')) return;

    const update = () => {
      this.#track.classList.toggle('swiper--at-start', swiper.isBeginning);
      this.#track.classList.toggle('swiper--at-end', swiper.isEnd);
    };

    swiper.on('slideChange', update);
    swiper.on('resize', update);
    swiper.on('reachBeginning', update);
    swiper.on('reachEnd', update);
    swiper.on('fromEdge', update);
    update();
  }
}

if (!customElements.get('legal-tab-group')) {
  customElements.define('legal-tab-group', LegalTabGroup);
}
